import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Scale, TrendingUp, ClipboardList, Filter, Thermometer, Droplets,
  Lightbulb, Mountain, Dna, Fish, Waves, PenTool, Wrench, CalendarCheck,
  Flower2, Users, FlaskConical, ShieldCheck, Cpu, ArrowRight, ArrowLeft,
  Check, ChevronRight,
} from "lucide-react";
import { Navbar } from "@/components/site/Navbar";

const TRANSITION_DURATION_MS = 1500;

const WHY = [
  { icon: Sparkles, title: "Advanced Reef Expertise", body: "From beginner reef systems to highly advanced mixed reefs, Seatech designs and maintains thriving marine ecosystems with precision and care." },
  { icon: Scale, title: "Built Around Stability", body: "Healthy reefs depend on consistency. Every system is engineered to provide stable water chemistry, proper flow, ideal lighting, and reliable filtration." },
  { icon: TrendingUp, title: "Long-Term Success", body: "Every aquarium is designed not only to look incredible today but to continue flourishing for years through professional maintenance and expert guidance." },
];

const STEPS = [
  { icon: ClipboardList, title: "Precision Planning", body: "Every reef aquarium begins with careful planning around tank size, equipment, livestock goals, and long-term system stability." },
  { icon: Filter, title: "Professional Filtration", body: "Mechanical, biological, and chemical filtration work together to maintain crystal-clear water and support a healthy reef ecosystem." },
  { icon: Thermometer, title: "Climate Stability", body: "Premium heating, temperature monitoring, and environmental control create the consistent conditions that sensitive reef inhabitants require." },
  { icon: Droplets, title: "Pure RODI Water", body: "Every reef aquarium begins with purified RODI water and premium conditioners to establish clean, stable water chemistry." },
  { icon: Lightbulb, title: "Reef Lighting", body: "Advanced LED lighting systems are carefully selected and programmed to support healthy coral growth while showcasing vibrant coloration." },
  { icon: Mountain, title: "Live Rock Aquascaping", body: "Premium reef-safe live rock is thoughtfully arranged to maximize biological filtration, coral placement, and natural visual flow." },
  { icon: Dna, title: "Biological Cycling", body: "The aquarium is patiently cycled until beneficial bacteria establish a stable biological foundation before sensitive livestock is introduced." },
  { icon: Fish, title: "Reef Livestock Introduction", body: "Reef-safe fish, invertebrates, and cleanup crews are gradually acclimated to create a balanced, healthy marine ecosystem." },
  { icon: Flower2, title: "Coral Growth & Long-Term Care", body: "Water movement, feeding schedules, nutrient management, trace elements, and premium additives are continually optimized to encourage healthy coral growth and vibrant coloration." },
];

const SERVICES = [
  { icon: PenTool, title: "Custom Reef Aquarium Design", body: "Professionally designed reef aquariums tailored to luxury homes, offices, restaurants, medical practices, and commercial spaces." },
  { icon: Wrench, title: "Reef Aquarium Installation", body: "Complete installation of aquariums, filtration systems, lighting, controllers, pumps, plumbing, and life-support equipment." },
  { icon: CalendarCheck, title: "Reef Tank Maintenance", body: "Comprehensive maintenance programs that keep reef systems healthy, balanced, and visually stunning throughout the year." },
  { icon: Flower2, title: "Coral Selection & Placement", body: "Expert guidance on selecting and positioning corals based on compatibility, lighting, flow, and long-term growth." },
  { icon: Users, title: "Reef Livestock Planning", body: "Professionally curated combinations of reef-safe fish, invertebrates, cleanup crews, and other marine life." },
  { icon: FlaskConical, title: "Water Chemistry Management", body: "Routine testing, dosing, supplementation, nutrient balancing, and RODI water preparation to maintain optimal reef conditions." },
];

const EXPERIENCE = [
  { title: "Vibrant Coral Ecosystems", body: "Watch colorful coral colonies mature into breathtaking living reef displays." },
  { title: "Intelligent Water Movement", body: "Strategically positioned flow systems replicate natural reef currents to support coral health and nutrient distribution." },
  { title: "Reef-Safe Livestock", body: "Every fish and invertebrate is selected for compatibility, ensuring a peaceful and balanced aquarium community." },
  { title: "Sustainable Coral Growth", body: "Lighting schedules, trace elements, and feeding strategies are continually refined to encourage healthy growth and vibrant coloration." },
];

const EXPERTISE = [
  { icon: FlaskConical, title: "Stable Water Chemistry", body: "Maintaining consistent water parameters is essential for coral health and long-term ecosystem stability." },
  { icon: Cpu, title: "Advanced Equipment", body: "Professional filtration, circulation, lighting, and monitoring systems create the conditions that reefs demand." },
  { icon: Flower2, title: "Expert Coral Care", body: "Corals require carefully managed flow, nutrition, lighting intensity, and trace elements to flourish." },
  { icon: ShieldCheck, title: "Ongoing Professional Maintenance", body: "Routine inspections, water testing, equipment optimization, and preventative care help ensure your reef continues to thrive." },
];

const GALLERY = [
  "https://images.unsplash.com/photo-1535591273668-578e31182c4f?q=85&w=1000",
  "https://images.pexels.com/photos/37703360/pexels-photo-37703360.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "https://images.pexels.com/photos/15932430/pexels-photo-15932430.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "https://images.unsplash.com/photo-1544552866-d3ed42536cfd?q=85&w=1000",
  "https://images.pexels.com/photos/35787439/pexels-photo-35787439.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "https://images.unsplash.com/photo-1596414086775-3e321ab08f36?q=85&w=1000",
  "https://images.unsplash.com/photo-1634813853938-e792dc26b05b?q=85&w=1000",
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 44, filter: "blur(6px)" }}
    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Particles = () => {
  const parts = useMemo(
    () =>
      Array.from({ length: 20 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: 4 + Math.random() * 16,
        duration: 12 + Math.random() * 12,
        delay: Math.random() * 12,
        coral: Math.random() > 0.6,
      })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {parts.map((p) => (
        <motion.span
          key={p.id}
          initial={{ y: "110%", opacity: 0 }}
          animate={{ y: "-15%", opacity: [0, 0.55, 0.55, 0] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: "linear" }}
          className={`absolute rounded-full backdrop-blur-[1px] ${p.coral ? "border border-[#ff7a52]/40 bg-[#ff7a52]/15" : "border border-[#35e0d1]/30 bg-[#35e0d1]/10"}`}
          style={{ left: `${p.left}%`, width: p.size, height: p.size }}
        />
      ))}
    </div>
  );
};

const LightRays = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden">
    {[12, 40, 68, 88].map((left, i) => (
      <motion.div
        key={i}
        animate={{ opacity: [0.04, 0.16, 0.04] }}
        transition={{ duration: 9 + i * 2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-1/3 h-[160%] w-36 rotate-12 bg-gradient-to-b from-[#7fe8ff]/40 via-[#35e0d1]/10 to-transparent blur-2xl"
        style={{ left: `${left}%` }}
      />
    ))}
  </div>
);

export default function ReefAquariums() {
  const [phase, setPhase] = useState("intro");
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
  }, []);

  useEffect(() => {
    if (phase !== "intro") return;
    const t = setTimeout(() => setPhase("page"), TRANSITION_DURATION_MS);
    return () => clearTimeout(t);
  }, [phase]);

  const goHome = (target) => {
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector(target);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { duration: 1.4 });
      else el.scrollIntoView({ behavior: "smooth" });
    }, 250);
  };

  return (
    <main data-testid="reef-page" className="relative min-h-screen bg-[var(--ink)] text-[var(--foam)]">
      {phase === "intro" && (
        <div className="fixed inset-0 z-[70] bg-[var(--ink)] cursor-pointer" data-testid="reef-intro" onClick={() => setPhase("page")}>
          <video
            className="absolute inset-0 h-full w-full object-cover"
            src="/media/reef-transition.mp4"
            autoPlay
            muted
            playsInline
            onEnded={() => setPhase("page")}
            data-testid="reef-transition-video"
          />
        </div>
      )}

      <div className="fixed inset-0 z-0">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src="/media/reef-loop.mp4"
          autoPlay
          loop
          muted
          playsInline
          data-testid="reef-loop-video"
        />
        <div className="absolute inset-0 bg-[#050c22]/80" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#040a1e]/88 via-[#071233]/68 to-[#03081c]/94" />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: phase === "page" ? 1 : 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10"
      >
        <Navbar />

        {/* HERO */}
        <section className="relative flex min-h-[58vh] items-center overflow-hidden px-6 lg:px-16 pt-52 pb-10">
          <LightRays />
          <Particles />
          <div className="relative mx-auto flex w-full max-w-4xl flex-col items-center text-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#35e0d1]/40 bg-[#35e0d1]/10 px-4 py-2 font-body text-xs font-medium uppercase tracking-[0.28em] text-[#7fe8ff]" data-testid="reef-kicker">
                World-Class Reef Exhibits
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 font-title text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl" data-testid="reef-headline">
                Custom Reef<br />Aquariums
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-2xl font-body text-base font-light leading-relaxed text-[var(--foam-dim)] lg:text-lg">
                Professionally designed, expertly maintained, and engineered to support thriving coral
                ecosystems. Seatech transforms homes and businesses with breathtaking reef aquariums
                built for lasting success.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => goHome("#contact")}
                  data-testid="reef-hero-cta"
                  className="group inline-flex items-center gap-3 rounded-full bg-[#35e0d1] px-8 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[#04221f] transition-all duration-300 hover:shadow-[0_0_40px_-8px_#35e0d1]"
                >
                  Schedule a Reef Consultation
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <button
                  onClick={() => document.getElementById("process")?.scrollIntoView({ behavior: "smooth" })}
                  data-testid="reef-hero-secondary"
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--foam)]/25 px-6 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[var(--foam)]/90 backdrop-blur-md transition-all duration-300 hover:border-[var(--foam)]/60"
                >
                  Explore Our Process
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        {/* WHY CHOOSE SEATECH */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Why Choose Seatech</h2>
            </Reveal>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {WHY.map((c, i) => (
                <Reveal key={c.title} delay={i * 0.12}>
                  <div
                    className="group h-full rounded-3xl border border-[var(--foam)]/12 bg-[#071233]/45 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#35e0d1]/45"
                    data-testid={`reef-why-card-${i}`}
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#35e0d1]/25 bg-[#35e0d1]/10 text-[#7fe8ff] transition-all duration-500 group-hover:shadow-[0_0_30px_-6px_#35e0d1]">
                      <c.icon className="h-7 w-7" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-title text-xl font-bold tracking-tight">{c.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)]">{c.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* THE SEATECH REEF STANDARD */}
        <section id="process" className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-5xl">
            <Reveal>
              <span className="font-body text-xs font-medium uppercase tracking-[0.28em] text-[#7fe8ff]">Our Process</span>
              <h2 className="mt-3 font-title text-3xl font-bold tracking-tight lg:text-4xl">The Seatech Reef Standard</h2>
            </Reveal>
            <div className="relative mt-14">
              <div className="absolute left-7 top-0 bottom-0 hidden w-px bg-gradient-to-b from-[#35e0d1]/50 via-[var(--foam)]/15 to-transparent md:block" />
              <div className="space-y-6">
                {STEPS.map((s, i) => (
                  <Reveal key={s.title} delay={i * 0.04}>
                    <div
                      className="group relative flex items-start gap-6 rounded-3xl border border-[var(--foam)]/12 bg-[#071233]/45 p-6 backdrop-blur-2xl transition-all duration-500 hover:border-[#35e0d1]/45 md:pl-8"
                      data-testid={`reef-step-${i}`}
                    >
                      <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[#35e0d1]/30 bg-[#35e0d1]/10 text-[#7fe8ff] transition-all duration-500 group-hover:shadow-[0_0_30px_-6px_#35e0d1]">
                        <s.icon className="h-6 w-6" strokeWidth={1.5} />
                      </div>
                      <div>
                        <span className="font-body text-xs font-medium uppercase tracking-[0.24em] text-[#7fe8ff]">Step {i + 1}</span>
                        <h3 className="mt-1 font-title text-lg font-bold tracking-tight">{s.title}</h3>
                        <p className="mt-2 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)]">{s.body}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* REEF AQUARIUM SERVICES */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Reef Aquarium Services</h2>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {SERVICES.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.08}>
                  <div
                    className="group relative h-full overflow-hidden rounded-3xl border border-[var(--foam)]/12 bg-[#071233]/45 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#35e0d1]/45 hover:shadow-[0_20px_60px_-30px_#35e0d1]"
                    data-testid={`reef-service-${i}`}
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#35e0d1]/25 bg-[#35e0d1]/10 text-[#7fe8ff]">
                      <s.icon className="h-7 w-7" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-title text-lg font-bold tracking-tight">{s.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)]">{s.body}</p>
                    <ChevronRight className="mt-5 h-5 w-5 text-[#ff7a52] opacity-0 transition-all duration-500 group-hover:translate-x-1 group-hover:opacity-100" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* THE LIVING REEF EXPERIENCE */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-5xl">The Living Reef Experience</h2>
            </Reveal>
            <div className="mt-14 space-y-10">
              {EXPERIENCE.map((e, i) => (
                <Reveal key={e.title} delay={0.05}>
                  <div
                    className={`grid items-center gap-8 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}
                    data-testid={`reef-experience-${i}`}
                  >
                    <div className="group relative aspect-[4/3] overflow-hidden rounded-3xl border border-[var(--foam)]/12 [direction:ltr]">
                      <img
                        src={GALLERY[i]}
                        alt={e.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#03081c]/70 via-transparent to-transparent" />
                    </div>
                    <div className="[direction:ltr]">
                      <h3 className="font-title text-2xl font-bold tracking-tight lg:text-3xl">{e.title}</h3>
                      <p className="mt-4 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)] lg:text-base">{e.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* WHY REEF AQUARIUMS REQUIRE EXPERTISE */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Why Reef Aquariums Require Expertise</h2>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {EXPERTISE.map((c, i) => (
                <Reveal key={c.title} delay={i * 0.1}>
                  <div
                    className="group h-full rounded-3xl border border-[var(--foam)]/12 bg-[#071233]/45 p-7 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#ff7a52]/45 hover:shadow-[0_20px_60px_-30px_#ff7a52]"
                    data-testid={`reef-expertise-${i}`}
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#35e0d1]/25 bg-[#35e0d1]/10 text-[#7fe8ff] transition-all duration-500 group-hover:border-[#ff7a52]/40 group-hover:text-[#ff9a78]">
                      <c.icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-5 font-title text-lg font-bold leading-snug tracking-tight">{c.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)]">{c.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="relative px-6 lg:px-16 py-32">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#020717]/80 to-[#020717]/95" />
          <div className="relative mx-auto max-w-3xl text-center">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-5xl" data-testid="reef-final-heading">
                Experience the Beauty of a Living Reef
              </h2>
              <p className="mx-auto mt-6 max-w-2xl font-body text-sm font-light leading-relaxed text-[var(--foam-dim)] lg:text-base">
                From custom reef installations to comprehensive long-term maintenance, Seatech Aquariums
                combines advanced marine expertise with exceptional craftsmanship to create thriving reef
                ecosystems that become the centerpiece of your home or business.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => goHome("#contact")}
                  data-testid="reef-final-cta"
                  className="inline-flex items-center gap-3 rounded-full bg-[#35e0d1] px-9 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[#04221f] transition-all duration-300 hover:shadow-[0_0_44px_-8px_#35e0d1]"
                >
                  Book a Reef Consultation
                </button>
                <button
                  onClick={() => goHome("#about")}
                  data-testid="reef-final-secondary"
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--foam)]/25 px-8 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[var(--foam)]/90 backdrop-blur-md transition-all duration-300 hover:border-[var(--foam)]/60"
                >
                  Explore Maintenance Programs
                </button>
              </div>
            </Reveal>
          </div>
        </section>
      </motion.div>
    </main>
  );
}
